import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useContext } from "react";
import { useMutation } from "react-relay";
import {
  useDuplicateBrandMutation$data,
  useDuplicateBrandMutation as useDuplicateBrandMutationType,
} from "./__generated__/useDuplicateBrandMutation.graphql";
import SnackbarContext from "../contexts/SnackbarContext";

type DuplicateBrandInput = {
  sourceBrandId: string;
  displayName: string;
  iosAppIdentifier?: string | null;
  androidAppIdentifier?: string | null;
};

const mutation = graphql`
  mutation useDuplicateBrandMutation($input: DuplicateBrandInput!) {
    duplicateBrand(input: $input) {
      brand {
        id
        isBrandAdmin
        displayName
        facebookConfig {
          id
          needsReauth
        }
        appleDeveloperAgreementsPendingSince
        showIosTosAlert
        tempoApiToken
        iosPushNotificationCredentialsRequireServiceKeyMigration
        androidPushNotificationRequiresFcmv1Migration
        appConfig {
          appState
          iosAppIdentifier
          androidAppIdentifier
        }
      }
    }
  }
`;

const useDuplicateBrandMutation = (): [
  (
    input: DuplicateBrandInput,
    onCompleted?: (data: useDuplicateBrandMutation$data) => void
  ) => void,
  boolean
] => {
  const [commitMutation, isMutationInFlight] =
    useMutation<useDuplicateBrandMutationType>(mutation);
  const snackbarContext = useContext(SnackbarContext);

  const onMutate = useCallback(
    (
      input: DuplicateBrandInput,
      onCompleted?: (data: useDuplicateBrandMutation$data) => void
    ) => {
      commitMutation({
        variables: { input },
        updater: (store) => {
          const result = store.getRootField("duplicateBrand");
          const newBrand = result?.getLinkedRecord("brand");
          if (!newBrand) {
            return;
          }

          const root = store.getRoot();
          const existingBrands = root.getLinkedRecords("internBrands") ?? [];
          if (
            existingBrands.some(
              (brand) => brand.getDataID() === newBrand.getDataID()
            )
          ) {
            return;
          }

          const nextBrands = [...existingBrands, newBrand].sort((a, b) =>
            String(a.getValue("displayName") ?? "").localeCompare(
              String(b.getValue("displayName") ?? "")
            )
          );
          root.setLinkedRecords(nextBrands, "internBrands");
        },
        onCompleted: (data, errors) => {
          if (errors) {
            snackbarContext?.openSnackbar("Brand duplication failed", "error");
          } else {
            onCompleted && onCompleted(data);
            snackbarContext?.openSnackbar("Brand duplicated", "success");
          }
        },
        onError: () => {
          snackbarContext?.openSnackbar("Brand duplication failed", "error");
        },
      });
    },
    [commitMutation, snackbarContext]
  );

  return [onMutate, isMutationInFlight];
};

export default useDuplicateBrandMutation;
